import { useCallback, useEffect, useState } from 'react';
import { Button, renderQrPngDataUrl } from '@cuito/ui';
import { locationsApi, type ShopEntryCodeView } from '../../api/locations';
import { readServerMessage } from '../../lib/apiError';
import { entryUrlText, qrImageSource } from '../../lib/locationList';

interface Props {
  shopId: string;
  shopName: string;
}

/**
 * 地点码面板（`04-api-contract.md` §6.1 `GET /shops/:id/entry-code`、`REQ-SHOP-003`）。
 *
 * **图像来源有两级，主次分明**：
 *   1. 首选响应里的 `qrImageUrl`——服务端渲染并落盘（Task 6），已含全局 `/api` 前缀
 *      （契约 §1.1），前端原样展示，不加也不去前缀。同一份内容由同一处生成，
 *      尺寸/纠错级别/静默区不会与前端实现漂移，打印与导出也走它。
 *   2. `qrImageUrl` 为空（该端点尚未就绪，或服务端本次没有生成图）时才用
 *      `packages/ui` 的 `renderQrPngDataUrl(entryUrl)` 现渲染：宁可自己渲染一张，
 *      也不给用户一张破图或一段空白。
 *
 * 是否回退的判定收在 `locationList.qrImageSource` 里（纯函数，可单测），
 * 组件本身不做字符串判断。渲染失败（载荷为空时渲染器按约定抛 `TypeError`）不报错：
 * 两条路都没有就显示占位。
 */
export default function EntryCodePanel({ shopId, shopName }: Props) {
  const [entry, setEntry] = useState<ShopEntryCodeView | null>(null);
  const [fallbackQr, setFallbackQr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const code = await locationsApi.getShopEntryCode(shopId);
      setEntry(code);
      // 只有服务端没给图时才现渲染：这一步是纯开销，能省就省
      setFallbackQr(
        qrImageSource(code.qrImageUrl, null) === null
          ? await renderQrPngDataUrl(code.entryUrl).catch(() => null)
          : null,
      );
    } catch (e) {
      setError(readServerMessage(e) ?? '地点码加载失败，请重试');
      setEntry(null);
      setFallbackQr(null);
    } finally {
      setLoading(false);
    }
  }, [shopId]);

  useEffect(() => {
    void load();
  }, [load]);

  const copy = async (): Promise<void> => {
    if (!entry) return;
    try {
      await navigator.clipboard.writeText(entry.entryUrl);
      setNotice('入口链接已复制');
    } catch {
      // 非安全上下文（http 局域网调试）没有 clipboard：退回让用户手动选中
      setNotice('复制失败，请手动选择链接复制');
    }
  };

  const qrSrc = entry === null ? null : qrImageSource(entry.qrImageUrl, fallbackQr);

  return (
    <div className="flex flex-col gap-3 rounded-lg border-2 border-black p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-gray-900">{shopName} · 地点码</h3>
        {entry?.printable === true && <span className="text-sm text-gray-500">可打印</span>}
      </div>

      {loading && <p className="py-8 text-center text-gray-500">加载中…</p>}
      {error && <p className="font-bold text-red-600">{error}</p>}

      {!loading && entry && (
        <div className="flex flex-wrap items-start gap-4">
          {qrSrc !== null ? (
            <img
              src={qrSrc}
              alt={`地点码 ${entry.locationCode}`}
              className="h-56 w-56 rounded-lg border-2 border-black bg-white object-contain p-2"
            />
          ) : (
            <div className="flex h-56 w-56 items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-500">
              地点码图片暂不可用，可先用入口链接
            </div>
          )}

          <div className="flex min-w-[16rem] flex-col gap-2">
            <p className="text-sm text-gray-500">地点码</p>
            <p className="text-2xl font-bold">{entry.locationCode}</p>
            <p className="text-sm text-gray-500">入口链接</p>
            <p className="break-all text-sm">{entryUrlText(entry.entryUrl)}</p>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => void copy()}>
                复制链接
              </Button>
              <Button size="sm" variant="secondary" onClick={() => void load()} disabled={loading}>
                刷新
              </Button>
            </div>
            {notice && <p className="text-sm text-gray-500">{notice}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
